import LocationUtils from "./location-utils";

export default class AjaxUtils {

    static get MAX_UPLOAD_SIZE() {
        return 20 * 1024 * 1024; // 20 MB
    }

    static get MAX_UPLOAD_SIZE_MB() {
        return this.MAX_UPLOAD_SIZE / 1024 / 1024;
    }

    /**
     * Make a post request to wordpress
     * @param action
     * @param formData
     * @param callback (err, data) => {}
     */
    static postRequest(action, formData, callback) {
        formData.append("action", action);

        const requestOptions = {
            method: 'POST',
            body: formData,
            redirect: 'follow',
        };

        fetch(window.wp_ajax.AJAX_URL, requestOptions)
            .then(response => {
                if(response.status === 413) {
                    throw new Error(`The files you uploaded were too large. Make sure the total size is less than ${this.MAX_UPLOAD_SIZE_MB} MB.`);
                }

                if(response.status !== 200) {
                    throw new Error(`An unexpected error occurred. Received a status code of ${response.status} from the server.`);
                }

                return response.json();
            })
            .then(result => {
                if(callback) {
                    callback(null, result);
                }
            })
            .catch(error => {
                if(callback) {
                    callback(error);
                }
            });
    }

    /**
     * Generates a payment intent, holds funds, saves
     * the warranty, then captures payment
     * @param amount
     * @param receiptEmail
     * @param projectData - array of project data
     * @param paymentMethodId - optional if paymentIntentId is present
     * @param paymentIntentId - optional if paymentMethodId is present
     * @param idempotencyKey
     * @param radarSession
     * @param callback
     */
    static finalizeTransaction(amount, receiptEmail, projectData, paymentMethodId, paymentIntentId, idempotencyKey, radarSession, callback) {
        const formData = new FormData();
        formData.append('amount', amount);
        formData.append('receipt_email', receiptEmail);
        formData.append('idempotency_key_create', idempotencyKey[0]);
        formData.append('idempotency_key_capture', idempotencyKey[1]);

        if(radarSession) {
            formData.append('radar_session', radarSession);
        }

        projectData.forEach((project, i) => {
            project.proofOfPurchaseFiles.forEach(file => {
                formData.append(`${project.projectName}[]`, file);
            });

            delete project.proofOfPurchaseFiles;

            formData.append(`projects[${i}]`, JSON.stringify(project));
        });

        if(paymentIntentId) {
            formData.append('payment_intent_id', paymentIntentId);
        }

        if(paymentMethodId) {
            formData.append('payment_method_id', paymentMethodId);
        }

        this.postRequest('malarkey_create_payment_intent', formData, callback);
    }

    static getWarrantyHistory(contractorId, callback, page = 1, perPage = 10) {
        const formData = new FormData();
        formData.append('contractor_id', contractorId);
        formData.append('page', String(page));
        formData.append('limit', String(perPage));

        this.postRequest('malarkey_ajax_get_contractor_warranties', formData, callback);
    }

    /**
     * Get contractor name/sugar id autocompletion
     *
     * @param search
     * @param callback
     */
    static getContractorAutocomplete(search, callback, lat = null, lng = null) {
        const formData = new FormData();
        formData.append('s', search);

        if(lat && lng) {
            formData.append('lat', lat);
            formData.append('lng', lng);
            this.postRequest('malarkey_contractor_autocomplete', formData, callback);
        } else {
            LocationUtils.getDeviceLocation(pos => {
                formData.append('lat', pos.coords.latitude.toString());
                formData.append('lng', pos.coords.longitude.toString());
                this.postRequest('malarkey_contractor_autocomplete', formData, callback);
            }, () => {
                formData.append('lat', '39.8283');
                formData.append('lng', '-98.5795');
                formData.append('radius', '50000');
                this.postRequest('malarkey_contractor_autocomplete', formData, callback);
            }, Infinity, 1000);
        }
    }

    /**
     * Submits a Custom Form
     * @param formId
     * @param formJson
     * @param callback (err, result) => {}
     * @param files - array of file pointers to upload
     */
    static submitForm(formId, formJson, callback, files = []) {
        this.submitFormWithPayment(formId, formJson, files, 0, '', null, null, null, callback, false);
    }

    /**
     * Submit a custom form with a payment
     * @param formId
     * @param formJson
     * @param files - array of file pointers for proof of purchase
     * @param paymentAmount
     * @param receiptEmail
     * @param paymentMethodId - optional if paymentIntentId is present
     * @param paymentIntentId - optional if paymentMethodId is present
     * @param radarSession
     * @param callback (err, result) => {}
     * @param hasPayment - optional, defaults to true
     * @param idempotencyKey
     */
    static submitFormWithPayment(formId, formJson, files = [], paymentAmount, receiptEmail, paymentMethodId, paymentIntentId, radarSession, callback, hasPayment = true, idempotencyKey = [null, null]) {
        const formData = new FormData();
        formData.append('form_id', formId);
        formData.append('form_data', JSON.stringify(formJson));
        formData.append('has_payment', hasPayment ? '1' : '0');
        formData.append('amount', paymentAmount);
        formData.append('receipt_email', receiptEmail);
        formData.append('radar_session', radarSession);
        formData.append('idempotency_key_create', idempotencyKey[0]);
        formData.append('idempotency_key_capture', idempotencyKey[1]);

        if(files) {
            files.forEach(file => {
                formData.append('files[]', file);
            });
        }

        if(paymentIntentId) {
            formData.append('payment_intent_id', paymentIntentId);
        }

        if(paymentMethodId) {
            formData.append('payment_method_id', paymentMethodId);
        }

        this.postRequest('malarkey_submit_form', formData, callback);
    }

    static verifyRecaptcha(token, action, callback) {
        const formData = new FormData();
        formData.append('token', token);
        formData.append('verify_action', action);

        this.postRequest('malarkey_verify_recaptcha', formData, callback);
    }
}